import { Instance, SnapshotIn, types, flow, getSnapshot } from 'mobx-state-tree';
import _ from 'lodash';
import moment from 'moment';

import { DataState, DataStateStore } from '@ace/core';

import { LATEST_VERSION_ID } from 'Integrations/domain/ProcedureVersionExportSelection';
import { IntegrationProcedure } from 'Integrations/domain/IntegrationProcedure';
import { DEFAULT_EXPORT_SETTINGS } from 'Integrations/domain/IntegrationExportSettings';
import { IntegrationsStore } from './Integrations.store';

const ExportSettingsStore = types.model('IntegrationExportSettingsStore', {
    split: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.split),
    createWbtCourse: types.optional(types.boolean, DEFAULT_EXPORT_SETTINGS.createWbtCourse),
    playerTemplate: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.playerTemplate),
    contentFolder: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.contentFolder),
    contentProvider: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.contentProvider),
    contentServer: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.contentServer),
    owner: types.optional(types.string, DEFAULT_EXPORT_SETTINGS.owner),
});

/** The free-text settings, i.e. everything except the `createWbtCourse` flag. */
export type IntegrationTextSettingField =
    | 'split'
    | 'playerTemplate'
    | 'contentFolder'
    | 'contentProvider'
    | 'contentServer'
    | 'owner';

export const IntegrationsUIStoreInferred = types
    .model('IntegrationsUIStoreInferred', {
        _integrations: types.optional(IntegrationsStore, {}),
        isModalOpened: types.optional(types.boolean, false),
        isExporting: types.optional(types.boolean, false),
        searchText: types.optional(types.string, ''),
        selectedDeploymentId: types.optional(types.string, ''),
        settings: types.optional(ExportSettingsStore, {}),
        // procedureId -> selected version id. A key being present *is* the
        // selection; LATEST_VERSION_ID means "resolve to the newest version".
        selection: types.map(types.string),
    })
    .volatile(() => ({
        status: DataStateStore.create({ state: DataState.initial }),
    }))
    .views(self => ({
        get procedures() { return self._integrations.proceduresList; },
        get deployments() { return self._integrations.deploymentsList; },
        get selectedCount() { return self.selection.size; },
        get isLoading() { return self.status.isLoading; },
    }))
    .views(self => ({
        get filteredProcedures() {
            const search = self.searchText.trim().toLowerCase();

            return search
                ? self.procedures.filter(procedure => procedure.name.toLowerCase().includes(search))
                : self.procedures;
        },
    }))
    .views(self => ({
        get canExport() {
            return !!self.selectedCount && !!self.selectedDeploymentId && !self.isExporting;
        },
        /** Procedures with no uploaded version have nothing to export. */
        get selectableFilteredProcedures() {
            return self.filteredProcedures.filter(procedure => !!procedure.versions.length);
        },
        isSelected(procedureId: string) { return self.selection.has(procedureId); },
        selectedVersion(procedureId: string) {
            return self.selection.get(procedureId) ?? LATEST_VERSION_ID;
        },
    }))
    .views(self => ({
        get areAllSelectableSelected() {
            return !!self.selectableFilteredProcedures.length
                && self.selectableFilteredProcedures.every(procedure => self.selection.has(procedure.id));
        },
    }))
    .actions(self => {
        const load = flow(function* () {
            self.status.setLoading();
            const [proceduresLoaded, deploymentsLoaded] = yield Promise.all([
                self._integrations.loadProcedures(),
                self._integrations.loadDeployments(),
            ]);

            if (proceduresLoaded) { self.status.setDone(); } else { self.status.setError(); }

            // Nothing to pick when there is a single target.
            if (deploymentsLoaded && self.deployments.length === 1) {
                self.selectedDeploymentId = self.deployments[0].id;
            }

            return proceduresLoaded;
        });

        const setSearchText = (searchText: string) => {
            self.searchText = searchText;
        };

        const selectDeployment = (deploymentId: string) => {
            self.selectedDeploymentId = deploymentId;
        };

        const setTextSetting = (field: IntegrationTextSettingField, value: string) => {
            self.settings[field] = value;
        };

        const toggleCreateWbtCourse = (createWbtCourse: boolean) => {
            self.settings.createWbtCourse = createWbtCourse;
        };

        const toggleProcedure = (procedureId: string, isSelected: boolean) => {
            if (isSelected) {
                self.selection.set(procedureId, LATEST_VERSION_ID);
            } else {
                self.selection.delete(procedureId);
            }
        };

        const setProcedureVersion = (procedureId: string, versionId: string) => {
            self.selection.set(procedureId, versionId);
        };

        const toggleAllFiltered = (isSelected: boolean) => {
            self.selectableFilteredProcedures.forEach(procedure => {
                if (isSelected) {
                    if (!self.selection.has(procedure.id)) {
                        self.selection.set(procedure.id, LATEST_VERSION_ID);
                    }
                } else {
                    self.selection.delete(procedure.id);
                }
            });
        };

        const clearSelection = () => {
            self.selection.clear();
        };

        /**
         * Builds the `ProceduresList` of the export body: the raw search
         * entities, each narrowed to the single selected version ("Latest"
         * resolves to the newest one at export time).
         *
         * Kept as an action rather than a view because it reads the raw
         * (non-snapshot) entities held in volatile state.
         */
        const getProceduresToExport = (): IntegrationProcedure[] => {
            const selections = Array.from(self.selection.entries());

            return selections.reduce<IntegrationProcedure[]>((accumulator, [procedureId, versionId]) => {
                const procedure = self._integrations.rawProcedures.find(raw => raw.id === procedureId);
                if (!procedure) { return accumulator; }

                const versions = _.orderBy(procedure.versions ?? [], ['date'], ['desc']);
                const version = versionId === LATEST_VERSION_ID
                    ? versions[0]
                    : versions.find(candidate => candidate.id === versionId);

                if (!version) { return accumulator; }

                accumulator.push({ ...procedure, versions: [version] });

                return accumulator;
            }, []);
        };

        const exportSelected = flow(function* () {
            const procedures = getProceduresToExport();

            if (!procedures.length || !self.selectedDeploymentId) {
                return { success: false, errorMessage: '' };
            }

            const deploymentName = self.deployments
                .find(deployment => deployment.id === self.selectedDeploymentId)?.name
                ?? self.selectedDeploymentId;
            const filename = `${deploymentName}_Procedures_${moment().format('YYMMDD_HHmm')}.csv`;

            self.isExporting = true;
            const result = yield self._integrations.exportProcedures(
                self.selectedDeploymentId,
                getSnapshot(self.settings),
                procedures,
                filename,
            );
            self.isExporting = false;

            return {
                success: !!result.success,
                errorMessage: result.error?.body?.messages?.[0] ?? '',
            };
        });

        return {
            load,
            setSearchText,
            selectDeployment,
            setTextSetting,
            toggleCreateWbtCourse,
            toggleProcedure,
            setProcedureVersion,
            toggleAllFiltered,
            clearSelection,
            getProceduresToExport,
            exportSelected,
        };
    })
    .actions(self => ({
        toggleModalOpened(isOpened: boolean) {
            self.isModalOpened = isOpened;

            if (isOpened) {
                self.load();
            } else {
                self.clearSelection();
                self.setSearchText('');
            }
        },
    }));

type IntegrationsUIStoreFactoryType = typeof IntegrationsUIStoreInferred;
interface IIntegrationsUIStoreFactory extends IntegrationsUIStoreFactoryType { }
export const IntegrationsUIStore: IIntegrationsUIStoreFactory = IntegrationsUIStoreInferred;
export interface IIntegrationsUIStore extends Instance<IIntegrationsUIStoreFactory> { }
export interface IIntegrationsUIStoreSnapshotIn extends SnapshotIn<IIntegrationsUIStore> { }
